/**
 * Monaco Editor providers for MethodScript language support.
 * Extracted from editor.ts for better code organization and type safety.
 */

import * as monaco from 'monaco-editor';
import { getContextAwareHover, getSignatureHelp } from '../public/shared-monaco/index.js';
import { findSIPrefixedValues, createSIPrefixHoverContent } from 'methodscript-core';

/**
 * Create a hover provider for MethodScript
 * Provides documentation for SI prefix literals and command documentation
 */
export function createMethodScriptHoverProvider(monacoInstance: typeof monaco): monaco.languages.HoverProvider {
    return {
        provideHover: (model: monaco.editor.ITextModel, position: monaco.Position): monaco.languages.ProviderResult<monaco.languages.Hover> => {
            const line = model.getLineContent(position.lineNumber);
            const column = position.column - 1;

            // First, check for SI prefix literals (e.g., 100G, 15m, 42k)
            // MethodScript: Floating-point values are signed integers followed by SI prefix (no decimal point)
            const siMatches = findSIPrefixedValues(line);
            for (const { match, index: matchIndex } of siMatches) {
                const matchEnd = matchIndex + match.length;

                // Check if cursor is within this match
                if (column >= matchIndex && column <= matchEnd) {
                    const content = createSIPrefixHoverContent(match);
                    if (content) {
                        return {
                            range: new monacoInstance.Range(position.lineNumber, matchIndex + 1, position.lineNumber, matchEnd + 1),
                            contents: [{ value: content.title }, { value: content.detail }],
                        };
                    }
                }
            }

            // If not an SI prefix, check for command documentation using context-aware hover
            const word = model.getWordAtPosition(position);
            if (!word) return null;

            // Use context-aware hover to detect if we're in a VarType parameter position
            const lineText = line;
            const wordStart = word.startColumn - 1; // Convert to 0-based
            const wordEnd = word.endColumn - 1; // Convert to 0-based
            const hoverDoc = getContextAwareHover(word.word, lineText, wordStart, wordEnd);

            if (hoverDoc) {
                return {
                    range: new monacoInstance.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
                    contents: hoverDoc.contents,
                };
            }

            return null;
        },
    };
}

/**
 * Create a signature help provider for MethodScript
 * Provides parameter hints for all commands with parameters
 */
export function createMethodScriptSignatureHelpProvider(): monaco.languages.SignatureHelpProvider {
    return {
        signatureHelpTriggerCharacters: [' '],
        provideSignatureHelp: (
            model: monaco.editor.ITextModel,
            position: monaco.Position,
        ): monaco.languages.ProviderResult<monaco.languages.SignatureHelpResult> => {
            const lineContent = model.getLineContent(position.lineNumber);
            const cursorColumn = position.column - 1; // Convert to 0-based

            const result = getSignatureHelp(lineContent, cursorColumn);

            if (!result) {
                return null;
            }

            return {
                value: {
                    signatures: result.signatures.map((sig) => ({
                        label: sig.label,
                        documentation: sig.documentation,
                        parameters: sig.parameters.map((param) => ({
                            label: param.label,
                            documentation: param.documentation,
                        })),
                    })),
                    activeSignature: result.activeSignature,
                    activeParameter: result.activeParameter,
                },
                dispose: () => {
                    // No cleanup needed
                },
            };
        },
    };
}

/**
 * Create a folding range provider for MethodScript
 * Provides code folding for blocks (if/endif, loop/endloop) and comment blocks
 */
export function createMethodScriptFoldingRangeProvider(monacoInstance: typeof monaco): monaco.languages.FoldingRangeProvider {
    return {
        provideFoldingRanges: (model: monaco.editor.ITextModel): monaco.languages.ProviderResult<monaco.languages.FoldingRange[]> => {
            const text = model.getValue();
            const lines = text.split('\n');
            const ranges: monaco.languages.FoldingRange[] = [];
            const stack: Array<{ start: number; type: string }> = [];

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();

                // Start of block
                if (line.startsWith('if ') || line.startsWith('loop') || line.startsWith('meas_loop_')) {
                    stack.push({ start: i, type: line.split(/\s+/)[0] });
                }

                // End of block
                if (line === 'endif' || line === 'endloop') {
                    if (stack.length > 0) {
                        const block = stack.pop()!;
                        ranges.push({
                            start: block.start + 1,
                            end: i + 1,
                            kind: monacoInstance.languages.FoldingRangeKind.Region,
                        });
                    }
                }

                // Comment blocks
                if (line.startsWith('#')) {
                    let commentEnd = i;
                    while (commentEnd + 1 < lines.length && lines[commentEnd + 1].trim().startsWith('#')) {
                        commentEnd++;
                    }
                    if (commentEnd > i) {
                        ranges.push({
                            start: i + 1,
                            end: commentEnd + 1,
                            kind: monacoInstance.languages.FoldingRangeKind.Comment,
                        });
                        i = commentEnd;
                    }
                }
            }

            return ranges;
        },
    };
}
